import React from 'react';
import {useLocation} from 'react-router-dom';

import type {Surface} from './surface';
import {ErrorState} from './ErrorState';
import {isAuthenticationError} from './identity/authError';
import {
  identityLoginUrl,
  redirectToIdentityLogin,
} from './identity/loginRedirect';
import {useViewer} from './identity/useViewer';
import {ShellLoader} from './ShellLoader';

export const RequireViewer = ({
  children,
  origins,
  redirectToIdentityLogin: redirectToLogin = redirectToIdentityLogin,
}: {
  children: React.ReactNode;
  origins?: Partial<Record<Surface, string>>;
  redirectToIdentityLogin?: typeof redirectToIdentityLogin;
}) => {
  const location = useLocation();
  const {viewer, loading, error} = useViewer({skip: false});
  const next = `${location.pathname}${location.search}${location.hash}`;
  const loginUrl = identityLoginUrl(origins, next);
  const authenticationError = isAuthenticationError(error);
  const redirectedRef = React.useRef(false);
  const needsLogin =
    !loading && (authenticationError || (!error && viewer === null));

  React.useEffect(() => {
    if (needsLogin && loginUrl && !redirectedRef.current) {
      redirectedRef.current = true;
      redirectToLogin(origins, next);
    }
  }, [needsLogin, loginUrl, next, origins, redirectToLogin]);

  if (loading) {
    return <ShellLoader label="Loading session" />;
  }

  if (needsLogin) {
    return loginUrl ? null : (
      <ErrorState role="status" title="Your session couldn't be loaded." />
    );
  }

  if (error) {
    return (
      <ErrorState role="status" title="Your session couldn't be loaded." />
    );
  }

  return <>{children}</>;
};
